import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { RESET_PASSWORD } from "@/constants/testIds";
import { CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError(t("resetPasswordUi.errorMissingToken"));
      return;
    }
    if (password.length < 6) {
      setError(t("resetPasswordUi.errorPasswordTooShort"));
      return;
    }
    if (password !== confirm) {
      setError(t("resetPasswordUi.errorPasswordsMismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success(t("resetPasswordUi.toastSuccess"));
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-8 bg-white">
      <div className="w-full max-w-md">
        <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-4">
          — {t("resetPasswordUi.eyebrow")}
        </div>
        <h1 className="font-display font-black text-4xl text-slate-900 tracking-tighter leading-tight">
          {t("resetPasswordUi.heading")}
        </h1>

        {!token && (
          <div className="mt-6 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
            {t("resetPasswordUi.missingToken")}
          </div>
        )}

        {done ? (
          <div
            data-testid={RESET_PASSWORD.successMessage}
            className="mt-8 flex items-start gap-3 p-4 rounded-lg border border-blue-200 bg-blue-50/60 text-slate-800"
          >
            <CheckCircle2 className="text-blue-800 shrink-0 mt-0.5" size={18} />
            <div className="text-sm">
              <div className="font-semibold">{t("resetPasswordUi.successTitle")}</div>
              <div className="mt-1">{t("resetPasswordUi.successRedirecting")}</div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="pwd" className="text-slate-700">{t("resetPasswordUi.newPasswordLabel")}</Label>
              <PasswordInput
                id="pwd"
                data-testid={RESET_PASSWORD.passwordInput}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="mt-2 h-11 bg-white"
              />
            </div>
            <div>
              <Label htmlFor="cpwd" className="text-slate-700">{t("resetPasswordUi.confirmPasswordLabel")}</Label>
              <PasswordInput
                id="cpwd"
                data-testid={RESET_PASSWORD.passwordConfirmInput}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={6}
                className="mt-2 h-11 bg-white"
              />
            </div>
            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                {error}
              </div>
            )}
            <Button
              type="submit"
              data-testid={RESET_PASSWORD.submitButton}
              disabled={loading || !token}
              className="w-full h-11 bg-blue-800 hover:bg-blue-700 text-white rounded-md"
            >
              {loading ? t("resetPasswordUi.buttonUpdating") : t("resetPasswordUi.buttonUpdate")}
            </Button>
          </form>
        )}

        <p className="mt-8 text-sm text-slate-600">
          {t("resetPasswordUi.backTo")}{" "}
          <Link to="/login" className="text-blue-800 font-semibold hover:underline">
            {t("resetPasswordUi.signInLink")}
          </Link>
        </p>
      </div>
    </div>
  );
}
